import { useModal } from "@/app/shared/modal-views/use-modal";
import ProjectDataSelect from "@/components/project-data-select";
import useProfile from "@/hooks/use-profile";
import {
  EImportErrorCode,
  EImportErrorStatus,
  ErrorStatusColors,
} from "@/utilities/enums/ImportData";
import ImportDataRepository from "@/utilities/repositories/ImportData";
import ProjectRepository from "@/utilities/repositories/Project";
import {
  IImportDataTask,
  IImportTaskErrorItem,
  IUnmappedProject,
} from "@/utilities/types/ImportData";
import { IProject, IProjectLocale } from "@/utilities/types/Project";
import { Roles } from "@/utilities/types/Users";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  PiXBold,
  PiLinkSimpleBold,
  PiCheckCircleBold,
  PiCaretLeftBold,
  PiCaretRightBold,
  PiCaretDownBold,
} from "react-icons/pi";
import {
  ActionIcon,
  Badge,
  Button,
  Loader,
  SelectOption,
  Text,
  Title,
  cn,
} from "rizzui";

type Props = {
  task: IImportDataTask;
  onSuccess?: () => void;
};

type TabKey = "unmapped" | "all";

const PAGE_SIZE = 20;

function getErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null) {
    const obj = err as Record<string, unknown>;
    if (typeof obj.message === "string") return obj.message;
  }
  return fallback;
}

export default function ErrorLogModal({ task, onSuccess }: Props) {
  const { closeModal } = useModal();
  const { profile } = useProfile();
  const [activeTab, setActiveTab] = useState<TabKey>("unmapped");

  // Mapping state
  const [mappingRawName, setMappingRawName] = useState<string | null>(null);
  const [expandedErrorRowId, setExpandedErrorRowId] = useState<string | null>(
    null,
  );
  const [selectedProject, setSelectedProject] = useState<SelectOption | null>(
    null,
  );
  const [isMappingLoading, setIsMappingLoading] = useState(false);
  const [isProjectOptionsLoading, setIsProjectOptionsLoading] = useState(true);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);

  // Unmapped projects state
  const [isUnmappedLoading, setIsUnmappedLoading] = useState(true);
  const [unmappedProjects, setUnmappedProjects] = useState<IUnmappedProject[]>(
    [],
  );

  // All errors state
  const [isErrorsLoading, setIsErrorsLoading] = useState(false);
  const [errors, setErrors] = useState<IImportTaskErrorItem[]>([]);
  const [errorTotal, setErrorTotal] = useState(0);
  const [errorPage, setErrorPage] = useState(1);
  const [hasLoadedErrors, setHasLoadedErrors] = useState(false);
  const isFetchingErrorsRef = useRef(false);

  const fetchUnmappedProjects = useCallback(async () => {
    try {
      setIsUnmappedLoading(true);
      const res = await ImportDataRepository.getUnmappedProjects(task.id);
      const rawData = res?.data?.data ?? res?.data;
      setUnmappedProjects(Array.isArray(rawData) ? rawData : []);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, "Failed to fetch unmapped projects"));
    } finally {
      setIsUnmappedLoading(false);
    }
  }, [task.id]);

  const fetchProjectOptions = useCallback(async () => {
    try {
      setIsProjectOptionsLoading(true);
      const agentId = profile?.role !== Roles.ADMIN ? profile?.id : undefined;
      let totalPage = 1;
      let currPage = 1;
      let options: SelectOption[] = [];

      do {
        const res = agentId
          ? await ProjectRepository.getAgentProject({
              queryParams: {
                pagination: {
                  limit: 50,
                  page: currPage,
                },
              },
            })
          : await ProjectRepository.getProject({
              queryParams: {
                pagination: {
                  limit: 50,
                  page: currPage,
                },
              },
            });

        const items = res.data.items ?? [];
        currPage = res.data.currentPage + 1;
        totalPage = res.data.totalPage;

        if (items.length > 0) {
          options = options.concat(
            items.map((item: IProject | IProjectLocale) => ({
              label:
                typeof item.name === "string"
                  ? item.name
                  : item.name?.en || "Unknown",
              value: item.id,
            })),
          );
        }
      } while (currPage <= totalPage);

      setProjectOptions(options);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, "Failed to fetch projects"));
    } finally {
      setIsProjectOptionsLoading(false);
    }
  }, [profile?.id, profile?.role]);

  const fetchErrors = useCallback(
    async (page = 1, force = false) => {
      if (isFetchingErrorsRef.current) return;
      if (!force && hasLoadedErrors && page === errorPage) return;

      try {
        isFetchingErrorsRef.current = true;
        setIsErrorsLoading(true);
        setExpandedErrorRowId(null);
        const res = await ImportDataRepository.getTaskErrors(task.id, {
          queryParams: {
            pagination: { limit: PAGE_SIZE, page },
          },
        });
        const payload = res?.data?.data ?? res?.data;
        setErrors(payload?.items || []);
        setErrorTotal(payload?.total || 0);
        setErrorPage(page);
        setHasLoadedErrors(true);
      } catch (err: unknown) {
        toast.error(getErrorMessage(err, "Failed to fetch error logs"));
      } finally {
        isFetchingErrorsRef.current = false;
        setIsErrorsLoading(false);
      }
    },
    [errorPage, hasLoadedErrors, task.id],
  );

  useEffect(() => {
    fetchUnmappedProjects();
  }, [fetchUnmappedProjects]);

  useEffect(() => {
    fetchProjectOptions();
  }, [fetchProjectOptions]);

  useEffect(() => {
    if (activeTab === "all" && !hasLoadedErrors) {
      fetchErrors(1);
    }
  }, [activeTab, fetchErrors, hasLoadedErrors]);

  useEffect(() => {
    setHasLoadedErrors(false);
    setErrors([]);
    setErrorTotal(0);
    setErrorPage(1);
    setExpandedErrorRowId(null);
  }, [task.id]);

  const totalPages = Math.ceil(errorTotal / PAGE_SIZE);

  const handleMapSubmit = async (rawProjectName: string) => {
    if (!selectedProject?.value) {
      toast.error("Please select an existing project");
      return;
    }

    try {
      setIsMappingLoading(true);
      const res = await ImportDataRepository.mapProjectAndReprocess({
        taskId: task.id,
        rawProjectName,
        projectId: String(selectedProject.value),
      });

      const mapPayload = res?.data?.data ?? res?.data;
      toast.success(
        mapPayload?.reprocessedCount
          ? `Successfully mapped and reprocessed ${mapPayload.reprocessedCount} row(s)!`
          : "Successfully mapped project and updated transactions!",
      );

      setMappingRawName(null);
      setExpandedErrorRowId(null);
      setSelectedProject(null);
      await fetchUnmappedProjects();
      if (activeTab === "all") {
        await fetchErrors(errorPage, true);
      }
      onSuccess?.();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, "Failed to map project"));
    } finally {
      setIsMappingLoading(false);
    }
  };

  const handleToggleMap = (rawProjectName: string) => {
    if (mappingRawName === rawProjectName) {
      setMappingRawName(null);
      setExpandedErrorRowId(null);
      setSelectedProject(null);
    } else {
      setMappingRawName(rawProjectName);
      setExpandedErrorRowId(null);
      setSelectedProject(null);
    }
  };

  const handleToggleErrorMap = (rowId: string, rawProjectName: string) => {
    if (expandedErrorRowId === rowId) {
      setMappingRawName(null);
      setExpandedErrorRowId(null);
      setSelectedProject(null);
    } else {
      setMappingRawName(rawProjectName);
      setExpandedErrorRowId(rowId);
      setSelectedProject(null);
    }
  };

  const pendingCount = unmappedProjects.reduce((sum, p) => sum + p.count, 0);

  return (
    <div className="block max-h-[85vh] overflow-y-auto rounded-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-5 md:p-6">
        <div>
          <Title
            as="h3"
            className="font-lexend text-xl font-semibold md:text-2xl"
          >
            Import Error Log
          </Title>
          <Text className="mt-1 text-xs text-gray-500">
            File:{" "}
            <span className="font-medium text-gray-800">{task.filename}</span>
          </Text>
        </div>
        <ActionIcon
          size="sm"
          variant="text"
          onClick={closeModal}
          className="p-0 text-gray-500 hover:!text-gray-900"
        >
          <PiXBold className="h-5 w-5" />
        </ActionIcon>
      </div>

      <div className="space-y-6 p-5 md:p-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-4">
            <Text className="text-xs text-amber-700">Unmapped Projects</Text>
            <Title as="h4" className="mt-1 text-lg font-bold text-amber-800">
              {pendingCount} row(s) in {unmappedProjects.length} project(s)
            </Title>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
            <Text className="text-xs text-gray-500">Total Failed Rows</Text>
            <Title as="h4" className="mt-1 text-lg font-bold text-gray-900">
              {task.failedRows ?? errorTotal}
            </Title>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            <button
              className={cn(
                "pb-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === "unmapped"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700",
              )}
              onClick={() => setActiveTab("unmapped")}
            >
              Unmapped Projects
            </button>
            <button
              className={cn(
                "pb-2 text-sm font-medium border-b-2 transition-colors",
                activeTab === "all"
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-500 hover:text-gray-700",
              )}
              onClick={() => setActiveTab("all")}
            >
              All Errors
            </button>
          </nav>
        </div>

        {/* Tab: Unmapped Projects */}
        {activeTab === "unmapped" ? (
          isUnmappedLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader variant="spinner" size="lg" />
              <Text className="mt-3 text-sm text-gray-500">
                Loading unmapped projects...
              </Text>
            </div>
          ) : unmappedProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
              <PiCheckCircleBold className="mb-2 h-10 w-10 text-emerald-500" />
              <Text className="font-medium text-gray-900">
                No Unmapped Projects
              </Text>
              <Text className="mt-1 text-xs text-gray-500">
                All projects in this file have been matched successfully.
              </Text>
            </div>
          ) : (
            <div className="space-y-2">
              {unmappedProjects.map((project) => {
                const isExpanded = mappingRawName === project.rawProjectName;
                return (
                  <div
                    key={project.rawProjectName}
                    className={cn(
                      "rounded-lg border bg-white transition-colors",
                      isExpanded
                        ? "border-primary/40 bg-primary-50/20"
                        : "border-gray-200",
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleToggleMap(project.rawProjectName)}
                      className="flex w-full items-center justify-between px-4 py-3 text-left"
                    >
                      <div>
                        <span className="font-semibold text-gray-900">
                          {project.rawProjectName}
                        </span>
                        <Text className="text-xs text-gray-500">
                          {project.count} row(s) affected
                        </Text>
                      </div>
                      <span className="inline-flex h-8 items-center rounded-md border border-gray-200 px-3 text-xs font-medium text-gray-700 transition-colors">
                        <PiLinkSimpleBold className="me-1 h-3.5 w-3.5" />
                        Map
                        <PiCaretDownBold
                          className={cn(
                            "ms-1 h-3 w-3 transition-transform duration-200",
                            isExpanded && "rotate-180",
                          )}
                        />
                      </span>
                    </button>
                    {isExpanded ? (
                      <div className="space-y-3 border-t border-gray-200 px-4 pb-4 pt-3">
                        <ProjectDataSelect
                          data={projectOptions}
                          label="Select Existing Project"
                          placeholder="Type or select project..."
                          size="md"
                          dropdownClassName="z-[99999999]"
                          disabled={isProjectOptionsLoading}
                          value={selectedProject ?? undefined}
                          onChange={(opt: SelectOption) =>
                            setSelectedProject(opt)
                          }
                        />
                        <div className="w-full">
                          <Button
                            size="md"
                            isLoading={isMappingLoading}
                            disabled={!selectedProject?.value}
                            onClick={() =>
                              handleMapSubmit(project.rawProjectName)
                            }
                            className="w-full"
                          >
                            <PiLinkSimpleBold className="me-1.5 h-4 w-4" />
                            Map & Reprocess
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )
        ) : null}

        {/* Tab: All Errors */}
        {activeTab === "all" ? (
          isErrorsLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader variant="spinner" size="lg" />
              <Text className="mt-3 text-sm text-gray-500">
                Loading error log...
              </Text>
            </div>
          ) : errors.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center">
              <PiCheckCircleBold className="mb-2 h-10 w-10 text-emerald-500" />
              <Text className="font-medium text-gray-900">
                No Error Rows Found
              </Text>
              <Text className="mt-1 text-xs text-gray-500">
                All transactions in this file have been processed successfully.
              </Text>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-gray-200 bg-gray-50 font-semibold text-gray-600">
                    <tr>
                      <th className="w-16 p-3 text-center">Row</th>
                      <th className="p-3">Project / Field</th>
                      <th className="p-3">Error Code</th>
                      <th className="p-3">Error Message</th>
                      <th className="w-28 p-3 text-center">Status</th>
                      <th className="p-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {errors.map((row) => {
                      const isUnmapped =
                        row.errorCode === EImportErrorCode.UNMAPPED_PROJECT;
                      const isPending =
                        row.status === EImportErrorStatus.PENDING;
                      const isExpanded =
                        isUnmapped &&
                        isPending &&
                        expandedErrorRowId === row.id;

                      return (
                        <Fragment key={row.id}>
                          <tr
                            className={cn(
                              "transition-colors hover:bg-gray-50/80",
                              isExpanded && "bg-primary-50/10",
                            )}
                          >
                            <td className="p-3 text-center font-mono font-medium text-gray-700">
                              #{row.rowIndex}
                            </td>
                            <td className="p-3">
                              <span className="font-semibold text-gray-900">
                                {row.rawProjectName || "N/A"}
                              </span>
                            </td>
                            <td className="p-3">
                              <Badge
                                variant="flat"
                                className="px-2 py-0.5 text-[10px] font-medium"
                              >
                                {row.errorCode}
                              </Badge>
                            </td>
                            <td className="max-w-xs truncate p-3 text-gray-600">
                              {row.errorMessage}
                            </td>
                            <td className="p-3 text-center">
                              <Badge
                                variant="flat"
                                className={cn(
                                  "rounded-full border px-2 py-0.5 text-[11px] font-medium",
                                  ErrorStatusColors[row.status],
                                )}
                              >
                                {row.status}
                              </Badge>
                            </td>
                            <td className="p-3 text-right">
                              {isUnmapped && isPending ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-8 px-3 text-xs"
                                  onClick={() =>
                                    handleToggleErrorMap(
                                      row.id,
                                      row.rawProjectName || "",
                                    )
                                  }
                                >
                                  <PiLinkSimpleBold className="me-1 h-3.5 w-3.5" />
                                  Map
                                  <PiCaretDownBold
                                    className={cn(
                                      "ms-1 h-3 w-3 transition-transform duration-200",
                                      isExpanded && "rotate-180",
                                    )}
                                  />
                                </Button>
                              ) : row.status === EImportErrorStatus.RESOLVED ? (
                                <span className="inline-flex items-center justify-end text-xs font-medium text-emerald-600">
                                  <PiCheckCircleBold className="me-1 h-4 w-4" />{" "}
                                  Mapped
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                          {isExpanded ? (
                            <tr className="bg-primary-50/10">
                              <td colSpan={6} className="p-0">
                                <div className="space-y-3 border-t border-primary/20 px-6 py-4">
                                  <ProjectDataSelect
                                    data={projectOptions}
                                    label="Select Existing Project"
                                    placeholder="Type or select project..."
                                    size="md"
                                    dropdownClassName="z-[99999999]"
                                    disabled={isProjectOptionsLoading}
                                    value={selectedProject ?? undefined}
                                    onChange={(opt: SelectOption) =>
                                      setSelectedProject(opt)
                                    }
                                  />
                                  <div className="w-full">
                                    <Button
                                      size="md"
                                      isLoading={isMappingLoading}
                                      disabled={!selectedProject?.value}
                                      onClick={() =>
                                        handleMapSubmit(
                                          row.rawProjectName || "",
                                        )
                                      }
                                      className="w-full"
                                    >
                                      <PiLinkSimpleBold className="me-1.5 h-4 w-4" />
                                      Map & Reprocess
                                    </Button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 ? (
                <div className="flex items-center justify-between pt-3">
                  <Text className="text-xs text-gray-500">
                    Page {errorPage} of {totalPages} ({errorTotal} total)
                  </Text>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={errorPage <= 1}
                      onClick={() => fetchErrors(errorPage - 1, true)}
                    >
                      <PiCaretLeftBold className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={errorPage >= totalPages}
                      onClick={() => fetchErrors(errorPage + 1, true)}
                    >
                      <PiCaretRightBold className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )
        ) : null}
      </div>
    </div>
  );
}
